import { apiClient, type ApiRequestConfig } from './api';

export interface RotatingPlayer {
  kind: 'USER' | 'GUEST' | 'CHILD'; userId: number | null; athleteId: number | null; displayName: string;
  adultConfirmed?: boolean;
  draftMissing?: boolean;
}
export interface RotatingSet {
  ordinal: number; revision: number; gamesA: number | null; gamesB: number | null;
  outcome: string; sideA: RotatingPlayer[]; sideB: RotatingPlayer[];
  format: string | null; tiebreakA: number | null; tiebreakB: number | null;
  changedAt: string; correctionReason: string | null;
}
export interface RotatingGroup {
  groupId: string; sessionId: number; revision: number; practice: boolean;
  sets: RotatingSet[]; history: RotatingSet[];
  matches?: Array<{matchKey:number;ordinal:number;confirmedAt?:string;practice?:boolean;sets:RotatingSet[];outcome:string}>;
}
export interface SessionMatchPlan {matchKey?:number;setOrdinals:number[];practice?:boolean}
export interface RotatingView { group: RotatingGroup; canCorrect: boolean }
export async function getRotatingSets(sessionId: number, expectedUserId: number | string, signal?: AbortSignal): Promise<RotatingView> {
  return (await apiClient.get<RotatingView>(`/api/v1/web/sessions/${sessionId}/rotating-sets`, {
    signal, headers: { 'X-TARK-Expected-User-Id': String(expectedUserId) },
  })).data;
}
export async function correctRotatingSets(sessionId: number, group: RotatingGroup, sets: RotatingSet[], reason: string,
  assertRequestCurrent: () => void, expectedUserId: number | string, matches?: SessionMatchPlan[]): Promise<RotatingGroup> {
  const player = (value: RotatingPlayer) => value.kind === 'USER' ? { kind: value.kind, userId: value.userId }
    : value.kind === 'CHILD' ? { kind: value.kind, athleteId: value.athleteId }
    : { kind: value.kind, guestName: value.displayName, adultConfirmed: value.adultConfirmed === true };
  return (await apiClient.put<RotatingGroup>(`/api/v1/web/sessions/${sessionId}/rotating-sets`, {
    clientMutationId: crypto.randomUUID(), expectedRevision: group.revision, correctionReason: reason.trim(),
    practice: group.practice,
    matches: matches ?? group.matches?.map(match=>({matchKey:match.matchKey,
      setOrdinals:match.sets.map(set=>set.ordinal),practice:group.practice})),
    sets: sets.map(set => ({ ordinal: set.ordinal, sideA: set.sideA.map(player), sideB: set.sideB.map(player),
      gamesA: set.gamesA, gamesB: set.gamesB, format: set.format, tiebreakA: set.tiebreakA, tiebreakB: set.tiebreakB })),
  }, { assertRequestCurrent, headers: { 'X-TARK-Expected-User-Id': String(expectedUserId) } } as ApiRequestConfig)).data;
}

export interface WebSessionScoreDraftPayload {
  slots:1|2; practice:boolean; chosenWinner:null; expectedResultRevision:number;
  matches:SessionMatchPlan[];
  activeMatch?:number; activeSets?:number[];
  sets:Array<{sideA:Array<WebDraftPlayer|null>;sideB:Array<WebDraftPlayer|null>;gamesA:string;gamesB:string;
    tiebreakA:string;tiebreakB:string;format:string}>;
}
interface WebDraftPlayer {kind:RotatingPlayer['kind'];name:string;userId?:number;athleteId?:number;guestName?:string;adultConfirmed?:boolean}
export interface WebSessionScoreDraft {revision:number;payload:WebSessionScoreDraftPayload}
export async function getSessionScoreDraft(sessionId:number,expectedUserId:string):Promise<WebSessionScoreDraft|null> {
  const response=await apiClient.get<WebSessionScoreDraft>(`/api/v1/web/sessions/${sessionId}/score-draft`,{
    headers:{'X-TARK-Expected-User-Id':expectedUserId},
  });
  return response.status===204?null:response.data;
}
export async function saveSessionScoreDraft(sessionId:number,expectedRevision:number,payload:WebSessionScoreDraftPayload,
  expectedUserId:string,assertRequestCurrent:()=>void):Promise<WebSessionScoreDraft> {
  return (await apiClient.put<WebSessionScoreDraft>(`/api/v1/web/sessions/${sessionId}/score-draft`,{expectedRevision,payload},{
    assertRequestCurrent,headers:{'X-TARK-Expected-User-Id':expectedUserId},
  } as ApiRequestConfig)).data;
}
